import { z } from 'zod';
import { laravelPaginator, looseBoolean, wireDecimal, type DecimalString } from './common';

/**
 * GET {pos}/branches — raw Laravel paginator with `areas` eager-loaded
 * (pos-api/app/Http/Controllers/Api/POS/BranchController.php:14-61).
 *
 * Serialization notes:
 * - `is_active` has NO boolean cast (pos-api/app/Models/Branch.php:35-40)
 *   → arrives as 0/1; delivery_enabled/pickup_enabled/disable_ordering ARE
 *   cast → true/false. looseBoolean absorbs both.
 * - areas rows are AreaBranch models: delivery_charges is a decimal string
 *   (pos-api/app/Models/AreaBranch.php:31-35).
 */

export const wireBranchAreaSchema = z.looseObject({
  id: z.number(),
  branch_id: z.number(),
  name: z.string(),
  delivery_charges: wireDecimal,
  active: looseBoolean.optional(),
  archived_at: z.string().nullish(),
});
export type WireBranchArea = z.infer<typeof wireBranchAreaSchema>;

export const wireBranchSchema = z.looseObject({
  id: z.number(),
  name: z.string(),
  slug: z.string().nullish(),
  country: z.string().nullish(),
  city: z.string().nullish(),
  is_active: looseBoolean,
  delivery_enabled: looseBoolean.optional(),
  pickup_enabled: looseBoolean.optional(),
  disable_ordering: looseBoolean.optional(),
  schedule: z.unknown().nullish(),
  areas: z.array(wireBranchAreaSchema).optional(),
});
export type WireBranch = z.infer<typeof wireBranchSchema>;

export const wireBranchesIndexSchema = laravelPaginator(wireBranchSchema);

// ---------------------------------------------------------------------------
// Normalized domain types
// ---------------------------------------------------------------------------

export interface BranchArea {
  id: number;
  branch_id: number;
  name: string;
  delivery_charges: DecimalString;
  active: boolean;
}

export interface Branch {
  id: number;
  name: string;
  slug: string | null;
  country: string | null;
  city: string | null;
  is_active: boolean;
  delivery_enabled: boolean;
  pickup_enabled: boolean;
  disable_ordering: boolean;
  schedule: unknown;
  /** Delivery areas with charges — used by the register's delivery flow. */
  areas: BranchArea[];
}

export function normalizeBranch(wire: WireBranch): Branch {
  return {
    id: wire.id,
    name: wire.name,
    slug: wire.slug ?? null,
    country: wire.country ?? null,
    city: wire.city ?? null,
    is_active: wire.is_active,
    delivery_enabled: wire.delivery_enabled ?? false,
    pickup_enabled: wire.pickup_enabled ?? false,
    disable_ordering: wire.disable_ordering ?? false,
    schedule: wire.schedule ?? null,
    areas: (wire.areas ?? [])
      .filter((a) => !a.archived_at)
      .map((a) => ({
        id: a.id,
        branch_id: a.branch_id,
        name: a.name,
        delivery_charges: a.delivery_charges,
        active: a.active ?? true,
      })),
  };
}
